import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import MDButton from "components/MDButton";
import { formatarDataCivilFinanceiro } from "../helpers/formatar-data-financeiro";
import { formatarMoedaBRL } from "../helpers/financeiro-formatters";
import { labelCategoriaDespesa } from "../helpers/extrato-movimentacao-labels";
import type { MovimentacaoFinanceiraView } from "../types/extrato-financeiro.types";
import {
  modalContentSx,
  modalHeaderSx,
  modalPaperSx,
  saveButtonSx,
} from "../../components/contas-financeiras.theme";

type Props = {
  movimentacao: MovimentacaoFinanceiraView | null;
  onClose: () => void;
};

function DetalheLinha({
  label,
  valor,
}: {
  label: string;
  valor: string | null | undefined;
}) {
  if (!valor?.trim()) return null;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500}>
        {valor}
      </Typography>
    </Box>
  );
}

export default function ExtratoMovimentacaoDetalheDialog({
  movimentacao,
  onClose,
}: Props) {
  const aberto = Boolean(movimentacao);

  return (
    <Dialog
      open={aberto}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: modalPaperSx }}
    >
      {movimentacao ? (
        <>
          <Box sx={modalHeaderSx}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <ReceiptLongOutlinedIcon sx={{ fontSize: 40, opacity: 0.9 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="overline" sx={{ opacity: 0.75, letterSpacing: 1 }}>
                  Detalhe da movimentação
                </Typography>
                <Typography variant="subtitle1" fontWeight={700} noWrap title={movimentacao.descricaoPrincipal}>
                  {movimentacao.descricaoPrincipal}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 0.75 }} flexWrap="wrap">
                  <Chip size="small" label={movimentacao.tipoLabel} sx={{ bgcolor: "rgba(255,255,255,0.12)", color: "#fff" }} />
                  <Chip size="small" label={movimentacao.origemLabel} sx={{ bgcolor: "rgba(255,255,255,0.08)", color: "#fff" }} />
                </Stack>
              </Box>
              <IconButton onClick={onClose} sx={{ color: "inherit" }} aria-label="Fechar">
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>

          <DialogContent sx={modalContentSx}>
            <Stack spacing={2}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "grey.50",
                  border: "1px solid",
                  borderColor: "grey.200",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Valor
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {formatarMoedaBRL(movimentacao.valor)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {movimentacao.dataMovimentacaoLabel} · {movimentacao.contaFinanceiraNome}
                  {movimentacao.contaDestinoNome ? ` → ${movimentacao.contaDestinoNome}` : ""}
                </Typography>
              </Box>

              {movimentacao.descricaoSubtitulo ? (
                <Typography variant="body2" color="text.secondary">
                  {movimentacao.descricaoSubtitulo}
                </Typography>
              ) : null}

              <Divider />

              <Stack spacing={1.5}>
                <DetalheLinha label="Descrição no sistema" valor={movimentacao.descricao} />
                <DetalheLinha label="Observação" valor={movimentacao.observacao} />
                <DetalheLinha label="Centro de custo" valor={movimentacao.centroCustoNome} />
              </Stack>

              {movimentacao.tituloPagarDescricao ||
              movimentacao.tituloReceberDescricao ||
              movimentacao.inscricaoNumero ||
              movimentacao.pedidoReferencia ? (
                <>
                  <Divider />
                  <Typography variant="subtitle2" fontWeight={700}>
                    Vínculo
                  </Typography>
                  <Stack spacing={1.5}>
                    {movimentacao.tituloPagarDescricao ? (
                      <>
                        <DetalheLinha label="Conta a pagar" valor={movimentacao.tituloPagarDescricao} />
                        <DetalheLinha label="Fornecedor" valor={movimentacao.tituloPagarFornecedor} />
                        <DetalheLinha
                          label="Categoria"
                          valor={labelCategoriaDespesa(movimentacao.tituloPagarCategoria)}
                        />
                        <DetalheLinha
                          label="Vencimento do título"
                          valor={
                            movimentacao.tituloPagarVencimento
                              ? formatarDataCivilFinanceiro(movimentacao.tituloPagarVencimento)
                              : null
                          }
                        />
                      </>
                    ) : null}
                    {movimentacao.tituloReceberDescricao ? (
                      <DetalheLinha label="Conta a receber" valor={movimentacao.tituloReceberDescricao} />
                    ) : null}
                    <DetalheLinha label="Inscrição" valor={movimentacao.inscricaoNumero} />
                    <DetalheLinha label="Pedido" valor={movimentacao.pedidoReferencia} />
                  </Stack>
                </>
              ) : null}

              {movimentacao.identificadorExterno ? (
                <>
                  <Divider />
                  <DetalheLinha label="Referência técnica" valor={movimentacao.identificadorExterno} />
                </>
              ) : null}
            </Stack>
          </DialogContent>

          <DialogActions
            sx={{
              px: { xs: 2.5, sm: 3 },
              py: 2,
              borderTop: "1px solid",
              borderColor: "grey.200",
            }}
          >
            <MDButton variant="gradient" color="dark" onClick={onClose} sx={saveButtonSx}>
              Fechar
            </MDButton>
          </DialogActions>
        </>
      ) : null}
    </Dialog>
  );
}
